import React, { useEffect } from 'react';
import { Form, message, Tooltip } from 'antd';

import { QuestionCircleOutlined } from '@ant-design/icons';

import { useTranslation } from 'react-i18next';
import { useFieldArray, useForm } from 'react-hook-form';

import { useDispatch } from 'react-redux';

import { hasFeature } from '../../../utils/restrictions';
import { showUpselling } from '../../../store/actions/paymentActions';

import { getLogicJumpData } from '../helpers';

import { DefaultJumpSection, LogicJumpSection, SequenceJumpSection } from './JumpSections';
import SettingsFooter from '../SettingsFooter';

import sequenceJumpIcon from '../../../assets/images/round-grey.png';
import logicJumpIcon from '../../../assets/images/logical-grey.png';
import defaultJumpIcon from '../../../assets/images/direct-grey.png';
import sequenceJumpActiveIcon from '../../../assets/images/round-green.png';
import logicJumpActiveIcon from '../../../assets/images/logical-green.png';
import defaultJumpActiveIcon from '../../../assets/images/direct-green.png';

import 'react-intl-tel-input/dist/main.css';

const ResponsesSettings = (props) => {
  const {
    reset,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { dirtyFields, isDirty },
  } = useForm({
    defaultValues: {
      responseJumpType: !hasFeature(props.plan, 'multiAgent')
        ? 'default'
        : props.form?.responseJumpType || 'default',
      whatsappNumber: props?.form.whatsappNumber || '',
      logicJumpData:
        props.form?.responseJumpType === 'logic'
          ? getLogicJumpData(
              props.questions,
              props.form?.formQuestions,
              props.form?.responseJumpData,
            )
          : [{ number: props.form?.whatsappNumber || '' }],
      sequenceJumpData:
        props.form?.responseJumpType === 'sequence'
          ? props.form?.responseJumpData || [{ number: props.form?.whatsappNumber || '' }]
          : [{ number: props.form?.whatsappNumber || '' }],
    },
  });

  const sequenceFieldArray = useFieldArray({
    control,
    name: 'sequenceJumpData',
  });

  const logicFieldArray = useFieldArray({
    control,
    name: 'logicJumpData',
  });
  const [responseJumpType, logicJumpData] = watch(['responseJumpType', 'logicJumpData']);

  props.dirtyFieldsRef.current = dirtyFields;

  const { t } = useTranslation();

  useEffect(() => {
    reset({
      responseJumpType: !hasFeature(props.plan, 'multiAgent')
        ? 'default'
        : props?.form?.responseJumpType || 'default',
      whatsappNumber: props?.form.whatsappNumber || '',
      logicJumpData:
        props?.form?.responseJumpType === 'logic'
          ? getLogicJumpData(
              props.questions,
              props.form?.formQuestions,
              props.form?.responseJumpData,
            )
          : [{ number: props.form?.whatsappNumber || '' }],
      sequenceJumpData:
        props?.form?.responseJumpType === 'sequence'
          ? props?.form?.responseJumpData || [{ number: props.form?.whatsappNumber || '' }]
          : [{ number: props.form?.whatsappNumber || '' }],
    });
  }, [props.form, reset, props.plan, props.questions]);

  function handleSave(data) {
    switch (responseJumpType) {
      case 'sequence':
        props.handleDispatch({
          responseJumpType: 'sequence',
          responseJumpData: data.sequenceJumpData,
        });
        break;
      case 'logic':
        props.handleDispatch({ responseJumpType: 'logic', responseJumpData: data.logicJumpData });
        break;
      case 'default':
        props.handleDispatch({
          responseJumpType: 'default',
          responseJumpData: [],
          whatsappNumber: data.whatsappNumber,
        });
        break;
      default:
    }
  }

  function handleError() {
    message.error("Couldn't update settings.");
  }

  const responseJumpMethods = {
    default: {
      title: t('formSettings.single'),
      desc: t('formSettings.defaultDesc'),
      icon: defaultJumpIcon,
      activeIcon: defaultJumpActiveIcon,
    },
    sequence: {
      title: t('formSettings.distribute'),
      desc: t('formSettings.sequenceDesc'),
      icon: sequenceJumpIcon,
      activeIcon: sequenceJumpActiveIcon,
    },
    logic: {
      title: t('formSettings.conditional'),
      desc: t('formSettings.logicDesc'),
      icon: logicJumpIcon,
      activeIcon: logicJumpActiveIcon,
    },
  };

  return (
    <div className="form-settings-screen">
      <div className="response-agents-section">
        <div>
          <div className="response-jump-selector-cards">
            {Object.keys(responseJumpMethods).map((responseJump) => (
              <JumpMethodCard
                responseJump={responseJump}
                activeResponseJumpType={responseJumpType}
                setValue={setValue}
                key={responseJump}
                responseJumpMethods={responseJumpMethods}
                plan={props.plan}
              />
            ))}
          </div>
          <Form colon={false}>
            {responseJumpType === 'default' && <DefaultJumpSection control={control} />}
            {responseJumpType === 'sequence' && (
              <SequenceJumpSection control={control} fieldArray={sequenceFieldArray} />
            )}
            {responseJumpType === 'logic' && (
              <LogicJumpSection
                control={control}
                fieldArray={logicFieldArray}
                form={props.form}
                logicJumpData={logicJumpData}
              />
            )}
          </Form>
        </div>
      </div>

      <SettingsFooter
        isDirty={isDirty}
        loading={props.isLoading}
        handleSubmit={handleSubmit(handleSave, handleError)}
      />
    </div>
  );
};

const JumpMethodCard = ({
  responseJump,
  activeResponseJumpType,
  setValue,
  responseJumpMethods,
  plan,
}) => {
  const isActive = responseJump === activeResponseJumpType;
  const dispatch = useDispatch();
  function handleClick() {
    if (!hasFeature(plan, 'multiAgent')) {
      dispatch(showUpselling('team'));
    } else {
      setValue('responseJumpType', responseJump, { shouldDirty: true });
    }
  }
  return (
    <div
      onClick={handleClick}
      className="jump-method-card"
      style={isActive ? { border: '2px solid #2db31f', backgroundColor: '#e4f2da80' } : {}}
    >
      <img
        src={
          isActive
            ? responseJumpMethods[responseJump].activeIcon
            : responseJumpMethods[responseJump].icon
        }
        width="50"
        height="50"
      />
      <div>
        <div className="jump-method-card-title">
          <span>{responseJumpMethods[responseJump].title}</span>
          <Tooltip
            title={
              <div style={{ fontSize: 'smaller' }}>{responseJumpMethods[responseJump].desc}</div>
            }
          >
            <QuestionCircleOutlined id="jump-card-icon" />
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export default ResponsesSettings;
